import React, { Component } from 'react'
import hoistNonReactStatics from 'hoist-non-react-statics'
import PropTypes from 'prop-types'
import { nameContextProvider, ContextProviderSubscriptions } from './ContextProvider'

export default function ContextSubscriber(...subscribers) {
  // normalize subscribers
  const { subscribersList, providerSubscriptionContextTypes, updateOnProps } = subscribers.reduce(
    function (data, subscriber) {
      const normalizedSubscriber =
        'string' == typeof subscriber
          ? { name: subscriber, mapToProps: 'all' }
          : null != subscriber.name
          ? subscriber
          : null
      if (null != normalizedSubscriber) {
        if (null != normalizedSubscriber.mapToProps && 'function' != typeof normalizedSubscriber.mapToProps) {
          const mapToProps = normalizedSubscriber.mapToProps
          normalizedSubscriber.mapToProps =
            'all' == mapToProps
              ? function (values) {
                  return values
                }
              : Array.isArray(mapToProps)
              ? function (values) {
                  return mapToProps.reduce(function (props, key) {
                    props[key] = values[key]
                    return props
                  }, {})
                }
              : 'object' == typeof mapToProps
              ? function (values) {
                  return Object.keys(mapToProps).reduce(function (props, key) {
                    props[mapToProps[key]] = values[key]
                    return props
                  }, {})
                }
              : null
        }
        data.subscribersList.push(normalizedSubscriber)
        if (null == normalizedSubscriber.provider) {
          data.providerSubscriptionContextTypes[nameContextProvider(normalizedSubscriber.name)] =
            PropTypes.instanceOf(ContextProviderSubscriptions)
        }
        if (normalizedSubscriber.mapToProps && normalizedSubscriber.updateOnProps) {
          const shouldUpdate =
            'function' == typeof normalizedSubscriber.updateOnProps
              ? normalizedSubscriber.updateOnProps
              : Array.isArray(normalizedSubscriber.updateOnProps)
              ? function (nextProps, prevProps) {
                  for (let i = 0; i < normalizedSubscriber.updateOnProps.length; i++) {
                    if (
                      nextProps[normalizedSubscriber.updateOnProps[i]] !=
                      prevProps[normalizedSubscriber.updateOnProps[i]]
                    ) {
                      return true
                    }
                  }
                  return false
                }
              : null
          if (shouldUpdate) {
            if (null == data.updateOnProps) {
              data.updateOnProps = []
            }
            data.updateOnProps.push({
              name: normalizedSubscriber.name,
              shouldUpdate,
              mapToProps: normalizedSubscriber.mapToProps
            })
          }
        }
      }
      return data
    },
    { subscribersList: [], providerSubscriptionContextTypes: {}, updateOnProps: null }
  )

  return function (SubscribedComponent) {
    if (0 == subscribersList.length) {
      // nothing to do
      return SubscribedComponent
    }

    // SubscribedComponent display name
    const subscribedComponentDisplayName = SubscribedComponent.displayName || SubscribedComponent.name || 'Component'

    // HOC class
    class ContextSubscriberComponent extends Component {
      // propTypes
      static propTypes = {
        saveRef: PropTypes.func
      }

      // find providers in react context
      static contextTypes = providerSubscriptionContextTypes

      // display name
      static displayName = `ContextSubscriber(${subscribedComponentDisplayName})`

      // ref to context provider options
      static ContextSubscribers = subscribers

      // put contextProps into state
      constructor(props, context) {
        super(props)
        this.saveWrappedRef = this.saveWrappedRef.bind(this)
        this.saveWrappedComponentRef = this.saveWrappedComponentRef.bind(this)
        this.contextValues = {}
        this.state = {
          contextProps: {}
        }
        this.unsubscribeListeners = subscribersList
          .reduce((unsubscribeListeners, subscriber) => {
            const provider = subscriber.provider
              ? subscriber.provider(props, context)
              : context[nameContextProvider(subscriber.name)]
            if (provider) {
              const subscription = contextValues => {
                if ('development' == process.env.NODE_ENV && subscriber.contextTypes) {
                  PropTypes.checkPropTypes(
                    subscriber.contextTypes,
                    contextValues,
                    `ContextSubscriber[${subscriber.name}]`,
                    subscribedComponentDisplayName
                  )
                }
                this.contextValues[subscriber.name] = contextValues
                if (subscriber.mapToProps) {
                  const mappedContextProps = subscriber.mapToProps.call(this, contextValues, this.props)
                  if (this.mounted) {
                    const mappedContextPropsKeys = Object.keys(mappedContextProps)
                    if (
                      mappedContextPropsKeys.some(
                        mappedContextPropsKey =>
                          mappedContextProps[mappedContextPropsKey] != this.state.contextProps[mappedContextPropsKey]
                      )
                    ) {
                      this.setState(
                        state => (state.contextProps = Object.assign(this.state.contextProps, mappedContextProps))
                      )
                    }
                  } else {
                    this.state.contextProps = Object.assign(this.state.contextProps, mappedContextProps)
                  }
                }
              }
              if (subscriber.contextTypes) {
                subscription.subscribeOn = Object.keys(subscriber.contextTypes)
              }
              unsubscribeListeners.push(provider.subscribe(subscription))
              return unsubscribeListeners
            }
            return null
          }, [])
          .filter(x => null != x)
      }

      // update on every props changes
      shouldComponentUpdate() {
        return true
      }

      componentDidMount() {
        this.mounted = true
      }

      // unsubscribe listeners on componentWillUnmount
      componentWillUnmount() {
        if (this.unsubscribeListeners && this.unsubscribeListeners.length) {
          this.unsubscribeListeners.forEach(unsubscribe => {
            unsubscribe()
          })
        }
        // unmount custom providers
        for (const subscriber of subscribersList) {
          if (subscriber.provider) {
            subscriber.provider?.providerUnmount?.()
          }
        }
        this.mounted = false
      }

      // update on props changes if needed
      UNSAFE_componentWillReceiveProps(nextProps) {
        if (updateOnProps) {
          const updatedContextProps = updateOnProps.reduce((props, { name, shouldUpdate, mapToProps }) => {
            if (shouldUpdate.call(this, nextProps, this.props)) {
              Object.assign(props, mapToProps.call(this, this.contextValues[name] || {}, nextProps))
            }
            return props
          }, {})
          if (Object.keys(updatedContextProps).length > 0) {
            this.setState({
              contextProps: Object.assign(this.state.contextProps, updatedContextProps)
            })
          }
        }
      }

      // save references from ManagedRenderComponent
      saveWrappedRef(name, element) {
        this[`ref${name}`] = element
        if (this.props.saveRef) {
          this.props.saveRef(name, element)
        }
      }

      // save wrapped component ref
      saveWrappedComponentRef(component) {
        this.refWrappedComponent = component
      }

      // render SubscribedComponent with component props and context props
      render() {
        const { contextProps } = this.state
        return (
          <SubscribedComponent
            saveRef={this.saveWrappedRef}
            ref={this.saveWrappedComponentRef}
            {...this.props}
            {...contextProps}
          />
        )
      }
    }

    // copy non React static members
    hoistNonReactStatics(ContextSubscriberComponent, SubscribedComponent)

    return ContextSubscriberComponent
  }
}
